'use client';

import Empty from '#/components/Empty';
import Network from '#/components/Network';
import NetworkUSDValue from '#/components/NetworkUSDValue';
import NetworkValue from '#/components/NetworkValue';
import Skeleton from '#/components/Skeleton';
import TableSortButton from '#/components/TableSortButton';
import { HelpTooltip } from '#/components/v2/help-tooltip';
import WatcherNetworkAPR from '#/components/WatcherNetworkAPR';
import WatcherNetworkPrice from '#/components/WatcherNetworkPrice';
import USDDecimal from '#/constants/USDDecimal';
import { useTranslation } from '#/contexts/useTranslation';
import useNetworks from '#/data/useNetworks';
import useWatcherNetworks, {
  WatcherNetworkInfo,
} from '#/data/useWatcherNetworks';
import useWatcherPriceList from '#/data/useWatcherPriceList';
import { cn } from '#/lib/utils';
import parseUnits from '#/utils/parseUnits';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingFns,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ethers } from 'ethers';
import { useCallback, useMemo, useState } from 'react';

export function StakingNetworksTable() {
  const translation = useTranslation();
  const { isLoading, data, mutate } = useWatcherNetworks();
  const dataSource = useMemo(() => data?.data.data ?? [], [data]);
  const mockDataSource = useMemo(() => Array.from({ length: 4 }), []);

  const sortingFn = useCallback(
    (
      rowA: Row<WatcherNetworkInfo>,
      rowB: Row<WatcherNetworkInfo>,
      columnId: string
    ) => {
      // @ts-ignore
      const valueA = rowA.original[columnId];
      // @ts-ignore
      const valueB = rowB.original[columnId];

      const result = ethers.FixedNumber.from(valueA).subUnsafe(
        ethers.FixedNumber.from(valueB)
      );
      return result.isZero() ? 0 : result.isNegative() ? -1 : 1;
    },
    []
  );
  const { data: networks } = useNetworks();
  const { data: watcherPriceList } = useWatcherPriceList();
  const valueSortingFn = useCallback(
    (
      rowA: Row<WatcherNetworkInfo>,
      rowB: Row<WatcherNetworkInfo>,
      columnId: string
    ) => {
      const networkA = networks?.data.data?.find(
        item => item.symbol === rowA.original.symbol
      );
      const networkB = networks?.data.data?.find(
        item => item.symbol === rowB.original.symbol
      );
      const priceA = watcherPriceList?.data.data?.find(
        item => item.symbol === rowA.original.symbol
      );
      const priceB = watcherPriceList?.data.data?.find(
        item => item.symbol === rowB.original.symbol
      );
      if (priceA && priceB && networkA && networkB) {
        // @ts-ignore
        const valueA = rowA.original[columnId];
        // @ts-ignore
        const valueB = rowB.original[columnId];

        const result = ethers.BigNumber.from(valueA)
          .mul(parseUnits(priceA.price, USDDecimal))
          .div(parseUnits('1', networkA.decimal))
          .sub(
            ethers.BigNumber.from(valueB)
              .mul(parseUnits(priceB.price, USDDecimal))
              .div(parseUnits('1', networkB.decimal))
          );
        return result.isZero() ? 0 : result.isNegative() ? -1 : 1;
      } else {
        return 0;
      }
    },
    [networks, watcherPriceList]
  );
  const priceSortingFn = useCallback(
    (rowA: Row<WatcherNetworkInfo>, rowB: Row<WatcherNetworkInfo>) => {
      const priceA = watcherPriceList?.data.data?.find(
        item => item.symbol === rowA.original.symbol
      );
      const priceB = watcherPriceList?.data.data?.find(
        item => item.symbol === rowB.original.symbol
      );
      if (priceA && priceB) {
        const result = parseUnits(priceA.price, USDDecimal).sub(
          parseUnits(priceB.price, USDDecimal)
        );
        return result.isZero() ? 0 : result.isNegative() ? -1 : 1;
      } else {
        return 0;
      }
    },
    [watcherPriceList]
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = useMemo<ColumnDef<WatcherNetworkInfo>[]>(
    () => [
      {
        id: 'symbol',
        enableSorting: false,
        cell: info => <Network symbol={info.row.original.symbol} />,
        header: translation.PortfolioDashboard.StakedAsset.HeadingStakedAsset,
        footer: props => props.column.id,
      },
      {
        id: 'apr',
        accessorFn: row => row.apr,
        sortingFn: 'sortingFn' as keyof SortingFns,
        cell: info => <WatcherNetworkAPR symbol={info.row.original.symbol} />,
        header: () => (
          <>
            <span className="align-middle">
              {translation.PortfolioDashboard.StakedAsset.HeadingAPY}
            </span>
            <HelpTooltip
              content={
                translation.PortfolioDashboard.StakedAsset.HeadingAPYHelp
              }
            />
          </>
        ),
        footer: props => props.column.id,
      },
      {
        id: 'tickerPrice',
        accessorFn: row => {},
        sortingFn: 'priceSortingFn' as keyof SortingFns,
        cell: info => <WatcherNetworkPrice symbol={info.row.original.symbol} />,
        header: () => (
          <>
            <span className="align-middle">
              {translation.PortfolioDashboard.StakedAsset.HeadingTokenPrice}
            </span>
            <HelpTooltip
              content={
                translation.PortfolioDashboard.StakedAsset.HeadingTokenPriceHelp
              }
            />
          </>
        ),
        footer: props => props.column.id,
      },
      {
        id: 'totalDelegated',
        accessorFn: row => row.totalDelegated,
        sortingFn: 'valueSortingFn' as keyof SortingFns,
        cell: info => (
          <dl className="flex flex-col space-y-1">
            <dt>
              <NetworkValue
                symbol={info.row.original.symbol}
                value={info.row.original.totalDelegated}
                maxDecimalDigits={5}
              />
            </dt>
            <dd className="text-gray-500 dark:text-gray-400">
              <NetworkUSDValue
                symbol={info.row.original.symbol}
                value={info.row.original.totalDelegated}
              />
            </dd>
          </dl>
        ),
        header: () => (
          <>
            <span className="align-middle">
              {
                translation.PortfolioDashboard.StakedAsset
                  .HeadingTotalStakedAmount
              }
            </span>
            <HelpTooltip
              content={
                translation.PortfolioDashboard.StakedAsset
                  .HeadingTotalStakedAmountHelp
              }
            />
          </>
        ),
        footer: props => props.column.id,
      },
      {
        id: 'totalReward',
        accessorFn: row => row.totalReward,
        sortingFn: 'valueSortingFn' as keyof SortingFns,
        cell: info => (
          <dl className="flex flex-col space-y-1">
            <dt>
              <NetworkValue
                symbol={info.row.original.symbol}
                value={info.row.original.totalReward}
                maxDecimalDigits={5}
              />
            </dt>
            <dd className="text-gray-500 dark:text-gray-400">
              <NetworkUSDValue
                symbol={info.row.original.symbol}
                value={info.row.original.totalReward}
              />
            </dd>
          </dl>
        ),
        header: () => (
          <>
            <span className="align-middle">
              {
                translation.PortfolioDashboard.StakedAsset
                  .HeadingAccumulatedRewards
              }
            </span>
            <HelpTooltip
              content={
                translation.PortfolioDashboard.StakedAsset
                  .HeadingAccumulatedRewardsHelp
              }
            />
          </>
        ),
        footer: props => props.column.id,
      },
    ],
    []
  );
  const table = useReactTable({
    data: dataSource,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortDescFirst: true,
    sortingFns: {
      sortingFn,
      valueSortingFn,
      priceSortingFn,
    },
  });

  return (
    <div className="bg-white dark:bg-gray-700 rounded-2xl space-y-6">
      <article className="overflow-auto">
        <table className="table-auto w-full text-gray-800 dark:text-gray-200 text-base">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                className="bg-slate-200 dark:bg-slate-600 rounded-lg"
              >
                {headerGroup.headers.map(header => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16"
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            'whitespace-nowrap flex items-center space-x-1 font-medium',
                            {
                              'cursor-pointer select-none':
                                header.column.getCanSort(),
                            }
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <TableSortButton
                              direction={header.column.getIsSorted()}
                            />
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading
              ? mockDataSource.map((_, i) => <SkeletonItem key={i} />)
              : table.getRowModel().rows.map(row => {
                  return (
                    <tr
                      key={row.id}
                      className="even:bg-slate-50 dark:even:bg-slate-600/25"
                    >
                      {row.getVisibleCells().map(cell => {
                        return (
                          <td
                            key={cell.id}
                            className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
          </tbody>
        </table>
        {!isLoading && dataSource.length === 0 && <Empty />}
      </article>
    </div>
  );
}

function SkeletonItem() {
  return (
    <tr className="even:bg-slate-50 dark:even:bg-slate-600/25">
      <td className="px-x py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-24">
        <Skeleton isLoading className="w-32" />
      </td>
      <td className="px-x py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-24">
        <Skeleton isLoading className="w-16" />
      </td>
      <td className="px-x py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-24">
        <Skeleton isLoading className="w-16" />
      </td>
      <td className="px-x py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-24">
        <dl className="flex flex-col space-y-1">
          <dt>
            <Skeleton isLoading className="w-32" />
          </dt>
          <dd className="text-gray-500 dark:text-gray-400">
            <Skeleton isLoading className="w-16" />
          </dd>
        </dl>
      </td>
      <td className="px-x py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-24">
        <dl className="flex flex-col space-y-1">
          <dt>
            <Skeleton isLoading className="w-32" />
          </dt>
          <dd className="text-gray-500 dark:text-gray-400">
            <Skeleton isLoading className="w-16" />
          </dd>
        </dl>
      </td>
    </tr>
  );
}
